import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  ComposedChart, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Users, ShieldAlert, Activity, Zap, DollarSign, TrendingUp, TrendingDown,
  Bell, Heart, Crown, ArrowDown, ArrowUp, AlertTriangle, Calendar
} from 'lucide-react';
import { T, CHART_COLORS, fmtMonth, fmtNum, fmtMoney, safeNum, pctChange, healthColor } from '../theme';
import { getGroupConfig } from '../riskConfig';
import { StatCard, Card, MiniBar, HealthBar, CustomTooltip, CoBadge, GroupBadge, HelpTip } from '../components/UI';

const CO_NAMES = { salam: "Al Salam", river: "River Prime" };

const MNAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Dashboard({ C, onClientClick, filterYear, setFilterYear, filterMonths, setFilterMonths }) {
  const shortNum = v => v >= 1e6 ? (v / 1e6).toFixed(1) + "M" : v >= 1e3 ? (v / 1e3).toFixed(0) + "K" : v;
  const [eqMode, setEqMode] = useState("drops");
  const [plMode, setPlMode] = useState("profit");
  const [volMode, setVolMode] = useState("volume");
  const [dnaFilter, setDnaFilter] = useState(null);
  const [alertFilter, setAlertFilter] = useState("all");
  const [ftdTab, setFtdTab] = useState("country");
  const [groupPopup, setGroupPopup] = useState(null);

  // Available years from data
  const availYears = [...new Set((C.allMonths || []).map(m => String(m).slice(0, 4)))].sort();
  // Available months for selected year
  const availMonthNums = (C.allMonths || [])
    .filter(m => filterYear === "all" || String(m).startsWith(filterYear))
    .map(m => parseInt(String(m).slice(4)));

  const toggleMonth = (num) => {
    if (filterMonths.includes(num)) setFilterMonths(filterMonths.filter(m => m !== num));
    else setFilterMonths([...filterMonths, num].sort((a, b) => a - b));
  };

  return (
    <div>
      {/* ═══ HEADER + DATE FILTER ═══ */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Dashboard</h1>
          <span style={{ color: T.textDim, fontSize: 13 }}>
            {C.companyFilter !== "all" ? (CO_NAMES[C.companyFilter] + " • ") : ""}
            {fmtMonth(C.latestMonth)} • {C.months.length} months
          </span>
        </div>
        {C.alerts.length > 0 && (
          <div onClick={() => document.getElementById("alerts-section")?.scrollIntoView({ behavior: "smooth" })}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 20, background: T.redBg, border: `1px solid ${T.red}30`, cursor: "pointer" }}>
            <Bell size={14} color={T.red} />
            <span style={{ fontSize: 12, color: T.red, fontWeight: 600 }}>{C.alerts.length} Alerts</span>
          </div>
        )}
      </div>

      {/* ═══ DATE FILTER BAR ═══ */}
      <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
        <Calendar size={16} color={T.cyan} style={{ flexShrink: 0 }} />

        {/* Year buttons */}
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
            style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
              background: filterYear === "all" ? T.cyan : T.bg, color: filterYear === "all" ? T.bgCard : T.textMuted }}>All</button>
          {availYears.map(y => (
            <button key={y} onClick={() => { setFilterYear(y); setFilterMonths([]); }}
              style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                background: filterYear === y ? T.cyan : T.bg, color: filterYear === y ? T.bgCard : T.textMuted }}>{y}</button>
          ))}
        </div>

        <div style={{ width: 1, height: 28, background: T.border, flexShrink: 0 }} />

        {/* Month pills */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1 }}>
          {MNAMES.map((name, i) => {
            const num = i + 1;
            const hasData = availMonthNums.includes(num);
            const isSelected = filterMonths.length === 0 || filterMonths.includes(num);
            return (
              <button key={num} onClick={() => hasData && toggleMonth(num)} disabled={!hasData}
                style={{ padding: "6px 12px", borderRadius: 6, border: "none", cursor: hasData ? "pointer" : "default", fontSize: 11, fontWeight: 600, transition: "all 0.15s",
                  background: !hasData ? "transparent" : (filterMonths.length === 0 ? `${T.cyan}15` : (isSelected ? T.cyan : T.bg)),
                  color: !hasData ? `${T.textMuted}40` : (filterMonths.length === 0 ? T.cyan : (isSelected ? T.bgCard : T.textMuted)),
                  opacity: hasData ? 1 : 0.3 }}>{name}</button>
            );
          })}
        </div>

        {/* Clear filter */}
        {(filterYear !== "all" || filterMonths.length > 0) && (
          <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
            style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* ═══ ROW 1: CLIENTS + RISK DISTRIBUTION + BEHAVIORAL/EQUITY RISK ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 14, marginBottom: 14, alignItems: "stretch" }}>
        {/* Total Clients */}
        <Card accent={T.cyan} style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 13, color: T.cyan, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>Total Clients (CU) <HelpTip id="cuAccount" /></div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 34, fontWeight: 800, color: T.text }}>{C.totalCU.toLocaleString()}</span>
                {C.prevTotalCU > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: pctChange(C.totalCU, C.prevTotalCU) >= 0 ? T.green : T.red }}>
                  {pctChange(C.totalCU, C.prevTotalCU) >= 0 ? "▲" : "▼"} {Math.abs(pctChange(C.totalCU, C.prevTotalCU)).toFixed(1)}%
                </span>}
              </div>
            </div>
            <Users size={24} color={T.cyan} />
          </div>
          <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
            {C.totalCU > 0 && <>
              <div style={{ width: (C.activeCU / C.totalCU * 100) + "%", background: T.green }} />
              <div style={{ width: (C.dormantCU / C.totalCU * 100) + "%", background: T.amber }} />
              <div style={{ width: (C.notActiveCU / C.totalCU * 100) + "%", background: T.red }} />
            </>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, flex: 1 }}>
            {[
              { label: "Active", count: C.activeCU, prev: C.prevActiveCU || 0, color: T.green },
              { label: "Dormant", count: C.dormantCU, prev: C.prevDormantCU || 0, color: T.amber },
              { label: "Not Active", count: C.notActiveCU, prev: C.prevNotActiveCU || 0, color: T.red },
            ].map(item => (
              <div key={item.label} style={{ background: T.bg, borderRadius: 10, padding: "20px 16px", borderLeft: `4px solid ${item.color}`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={{ fontSize: 13, color: item.color, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, fontWeight: 700 }}>{item.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: T.text, marginBottom: 6 }}>{item.count.toLocaleString()}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14, color: T.textDim, fontWeight: 600 }}>{C.totalCU > 0 ? (item.count / C.totalCU * 100).toFixed(1) : 0}%</span>
                  {item.prev > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: pctChange(item.count, item.prev) >= 0 ? T.green : T.red }}>
                    {pctChange(item.count, item.prev) >= 0 ? "▲" : "▼"} {Math.abs(pctChange(item.count, item.prev)).toFixed(1)}%
                  </span>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Risk Distribution (replaced Avg Broker Risk) ── */}
        <Card accent={T.amber} style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 13, color: T.amber, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>Risk Distribution <HelpTip id="riskLevels" /></div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Broker Risk Score</div>
            </div>
            <ShieldAlert size={22} color={T.amber} />
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {[
            { label: "Critical (51+)", count: C.healthDist.critical, color: T.red },
            { label: "High (39-50)", count: C.healthDist.high, color: T.orange },
            { label: "Elevated (27-38)", count: C.healthDist.elevated, color: T.amber },
            { label: "Low (16-26)", count: C.healthDist.low, color: T.cyan },
            { label: "Safe (0-15)", count: C.healthDist.safe, color: T.green },
          ].map(h => (
            <div key={h.label} style={{ marginBottom: 2 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 5 }}>
                <span style={{ color: h.color, fontWeight: 700 }}>{h.label}</span>
                <span style={{ color: T.text, fontWeight: 800, fontSize: 16 }}>{h.count} <span style={{ color: T.textDim, fontWeight: 400, fontSize: 12 }}>({(h.count / Math.max(C.clients.length, 1) * 100).toFixed(0)}%)</span></span>
              </div>
              <div style={{ height: 10, background: T.bgHover, borderRadius: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", width: Math.max((h.count / Math.max(C.clients.length, 1) * 100), h.count > 0 ? 1 : 0) + "%", background: h.color, borderRadius: 5 }} />
              </div>
            </div>
          ))}
          </div>
        </Card>

        {/* ── Behavioral + Equity Risk Combined ── */}
        <Card accent={T.red} style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 13, color: T.red, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>Risk Overview <HelpTip id="behavioralRisk" /></div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Behavioral + Equity</div>
            </div>
            <AlertTriangle size={22} color={T.red} />
          </div>
          {/* Behavioral Risk */}
          <div style={{ background: T.bg, borderRadius: 10, padding: "20px 18px", marginBottom: 10, borderLeft: `4px solid ${T.red}`, flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 13, color: T.red, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, fontWeight: 700 }}>Behavioral High Risk</div>
              <div className="mono" style={{ fontSize: 40, fontWeight: 800, color: T.red }}>{C.highRisk.toLocaleString()}</div>
            </div>
            {C.prevHighRisk > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 16, fontWeight: 700, padding: "8px 14px", borderRadius: 20,
                background: pctChange(C.highRisk, C.prevHighRisk) > 0 ? T.redBg : `${T.green}15`,
                color: pctChange(C.highRisk, C.prevHighRisk) > 0 ? T.red : T.green }}>
                {pctChange(C.highRisk, C.prevHighRisk) >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                {Math.abs(pctChange(C.highRisk, C.prevHighRisk)).toFixed(1)}%
              </div>
            )}
          </div>
          {/* Equity Risk */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ background: T.bg, borderRadius: 10, padding: "20px 16px", borderLeft: `4px solid ${T.red}` }}>
              <div style={{ fontSize: 13, color: T.red, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>Equity High <HelpTip id="equityRisk" /></div>
              <div className="mono" style={{ fontSize: 34, fontWeight: 800, color: T.text }}>{C.highEquity}</div>
              {C.prevHighEquity > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: pctChange(C.highEquity, C.prevHighEquity) >= 0 ? T.red : T.green, marginTop: 4 }}>
                {pctChange(C.highEquity, C.prevHighEquity) >= 0 ? "▲" : "▼"} {Math.abs(pctChange(C.highEquity, C.prevHighEquity)).toFixed(1)}%
              </div>}
            </div>
            <div style={{ background: T.bg, borderRadius: 10, padding: "20px 16px", borderLeft: `4px solid ${T.orange}` }}>
              <div style={{ fontSize: 13, color: T.orange, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, fontWeight: 700 }}>Equity Moderate</div>
              <div className="mono" style={{ fontSize: 34, fontWeight: 800, color: T.text }}>{C.modEquity}</div>
              {C.prevModEquity > 0 && <div style={{ fontSize: 12, fontWeight: 600, color: pctChange(C.modEquity, C.prevModEquity) >= 0 ? T.red : T.green, marginTop: 4 }}>
                {pctChange(C.modEquity, C.prevModEquity) >= 0 ? "▲" : "▼"} {Math.abs(pctChange(C.modEquity, C.prevModEquity)).toFixed(1)}%
              </div>}
            </div>
          </div>
        </Card>
      </div>

      {/* ═══ GROUP DISTRIBUTION BAR ═══ */}
      {C.groupDist && (() => {
        const gcfg = getGroupConfig();
        const total = Math.max(C.clients.length, 1);
        return (
          <Card style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.text, textTransform: "uppercase", letterSpacing: 0.8, display: "flex", alignItems: "center", gap: 6 }}>Client Group Distribution <HelpTip id="groupSystem" /></span>
              <span style={{ fontSize: 11, color: T.textDim }}>{C.clients.length} clients</span>
            </div>
            <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
              {(gcfg.groupOrder || Object.keys(gcfg.groups)).map(gk => {
                const g = gcfg.groups[gk]; if (!g) return null;
                const cnt = C.groupDist[gk] || 0;
                const pct = cnt / total * 100;
                return pct > 0 ? <div key={gk} title={`${gk}: ${cnt} (${pct.toFixed(1)}%)`} style={{ width: pct + "%", background: g.color, minWidth: cnt > 0 ? 2 : 0 }} /> : null;
              })}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {(gcfg.groupOrder || Object.keys(gcfg.groups)).map(gk => {
                const g = gcfg.groups[gk]; if (!g) return null;
                const cnt = C.groupDist[gk] || 0;
                return (
                  <span key={gk} onClick={() => setGroupPopup(gk)} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer", padding: "2px 6px", borderRadius: 4, background: "transparent", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = g.color + "15"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: g.color, display: "inline-block" }} />
                    <span style={{ color: g.color, fontWeight: 700 }}>{gk}</span>
                    <span style={{ color: T.textDim }}>{cnt}</span>
                    <span style={{ color: T.textMuted, fontSize: 10 }}>({(cnt / total * 100).toFixed(0)}%)</span>
                  </span>
                );
              })}
            </div>
            {/* Group Info Popup */}
            {groupPopup && (() => {
              const g = gcfg.groups[groupPopup]; if (!g) return null;
              const cnt = C.groupDist[groupPopup] || 0;
              return (
                <div onClick={() => setGroupPopup(null)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                  <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 440, maxHeight: "80vh", overflowY: "auto", background: T.bgCard, border: `1px solid ${g.color}40`, borderRadius: 14, padding: 24, boxShadow: `0 20px 60px rgba(0,0,0,0.5)` }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 14, height: 14, borderRadius: 4, background: g.color }} />
                        <span style={{ fontSize: 18, fontWeight: 700, color: g.color }}>{groupPopup}</span>
                        <span className="mono" style={{ fontSize: 11, color: T.textDim, background: T.bg, padding: "2px 8px", borderRadius: 4 }}>{cnt} clients</span>
                      </div>
                      <span onClick={() => setGroupPopup(null)} style={{ cursor: "pointer", color: T.textMuted, fontSize: 20, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, background: T.bg }}>×</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>{g.name}</div>
                    <div style={{ fontSize: 12, color: T.textDim, marginBottom: 16, lineHeight: 1.5 }}>{g.description}</div>
                    {g.actions && g.actions.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.amber, marginBottom: 6, letterSpacing: 1 }}>Recommended Actions</div>
                        {g.actions.map((act, ai) => (
                          <div key={ai} style={{ fontSize: 12, color: T.text, background: T.amber + "10", border: `1px solid ${T.amber}15`, borderRadius: 6, padding: "6px 12px", marginBottom: 3, lineHeight: 1.4 }}>• {act}</div>
                        ))}
                      </div>
                    )}
                    {g.rules && (g.rules.conditions?.length > 0 || g.rules.matchClientTypes?.length > 0) && (
                      <div>
                        <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: T.cyan, marginBottom: 6, letterSpacing: 1 }}>Classification Rules <span style={{ color: T.textMuted }}>({g.rules.logic || "AND"})</span></div>
                        {g.rules.matchClientTypes?.length > 0 && (
                          <div style={{ fontSize: 11, color: T.textDim, padding: "6px 10px", marginBottom: 3, background: T.bg, borderRadius: 6, borderLeft: `3px solid ${T.cyan}30` }}>
                            Client Type: {g.rules.matchClientTypes.join(", ")}
                          </div>
                        )}
                        {(g.rules.conditions || []).map((cond, ci) => (
                          <div key={ci} style={{ fontSize: 11, color: T.textDim, padding: "6px 10px", marginBottom: 3, background: T.bg, borderRadius: 6, borderLeft: `3px solid ${T.cyan}30` }}>
                            {cond.type}{cond.value !== undefined ? ` ≥ ${cond.value}` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </Card>
        );
      })()}

      {/* ═══ ROW 2: 6 FINANCIAL CARDS ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Volume", key: "volume", color: T.amber, icon: <Activity size={22} />, good: true, isMoney: false },
          { label: "Profit", key: "profit", color: T.green, icon: <TrendingUp size={22} />, good: true, isMoney: true },
          { label: "Equity", key: "equity", color: T.cyan, icon: <Activity size={22} />, good: true, isMoney: true },
          { label: "Deposit", key: "deposit", color: T.green, icon: <DollarSign size={22} />, good: true, isMoney: true },
          { label: "Withdraw", key: "withdraw", color: T.red, icon: <TrendingDown size={22} />, good: false, isMoney: true },
          { label: "Credit", key: "credit", color: T.purple, icon: <Zap size={22} />, good: true, isMoney: true },
        ].map(card => {
          const f = C.finCards?.[card.key] || { filtered: 0, total: 0, prev: 0 };
          const pctTotal = f.total > 0 ? (f.filtered / f.total * 100).toFixed(1) : "0";
          const prevPct = f.prev > 0 ? pctChange(f.filtered, f.prev) : 0;
          const fmt = v => card.isMoney ? fmtMoney(v) : fmtNum(v);
          const isNeg = f.filtered < 0;
          return (
            <div key={card.key} style={{ background: T.bgCard, borderRadius: 12, border: `1px solid ${T.border}`, padding: "16px 14px 14px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${card.color}, transparent)` }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, color: card.color, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{card.label}</div>
                <span style={{ color: card.color, opacity: 0.8 }}>{card.icon}</span>
              </div>
              <div className="mono" style={{ fontSize: 22, fontWeight: 800, color: isNeg ? T.red : T.text, marginBottom: 4 }}>{fmt(f.filtered)}</div>
              <div style={{ fontSize: 12, color: T.textDim, marginBottom: 8 }}>
                Total: <span style={{ color: f.total < 0 ? T.red : T.text, fontWeight: 700 }}>{fmt(f.total)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, paddingTop: 8, borderTop: `1px solid ${T.border}30` }}>
                <span style={{ fontSize: 11, color: T.textDim, fontWeight: 500 }}>{pctTotal}%</span>
                <span style={{ color: `${T.border}80` }}>|</span>
                {f.prev > 0 ? (
                  <span style={{ fontSize: 12, fontWeight: 700, color: (card.good ? prevPct >= 0 : prevPct <= 0) ? T.green : T.red }}>
                    {prevPct >= 0 ? "▲" : "▼"} {Math.abs(prevPct).toFixed(1)}%
                  </span>
                ) : <span style={{ fontSize: 12, color: T.textMuted }}>—</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* ═══ ROW 3: VIP + VOLUME/TRADES + EQUITY + PROFIT/LOSS ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 24 }}>

        {/* ── VIP Clients ── */}
        <Card accent={T.purple}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>VIP Clients</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>Top 10 by Equity</div>
            </div>
            <Crown size={16} color={T.purple} />
          </div>
          <div>
            {C.vipClients.slice(0, 10).map((c, i) => (
              <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CoBadge co={c._company} />
                  <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                </div>
                <span style={{ color: T.purple, fontWeight: 600, fontSize: 10 }}>{fmtMoney(safeNum(c.Equity))}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Volume / Trades Toggle ── */}
        <Card accent={volMode === "volume" ? T.cyan : T.amber}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Top 10 {volMode === "volume" ? "Volume" : "Trades"}</div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setVolMode("volume")} style={{ background: volMode === "volume" ? T.cyan : T.bgCard, border: `1px solid ${volMode === "volume" ? T.cyan : T.border}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 10, color: volMode === "volume" ? "#fff" : T.textMuted, fontWeight: 600 }}>
                Volume
              </button>
              <button onClick={() => setVolMode("trades")} style={{ background: volMode === "trades" ? T.amber : T.bgCard, border: `1px solid ${volMode === "trades" ? T.amber : T.border}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 10, color: volMode === "trades" ? "#fff" : T.textMuted, fontWeight: 600 }}>
                Trades
              </button>
            </div>
          </div>
          <div>
            {(volMode === "volume" ? C.topVolume : (C.topTrades || [])).slice(0, 10).map((c, i) => (
              <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CoBadge co={c._company} />
                  <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                </div>
                <span className="mono" style={{ color: volMode === "volume" ? T.cyan : T.amber, fontWeight: 600, fontSize: 10 }}>
                  {volMode === "volume" ? fmtNum(safeNum(c.Volume), 1) : fmtNum(safeNum(c.TotalTrades), 0)}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Equity Drops/Gains Toggle ── */}
        <Card accent={eqMode === "drops" ? T.red : T.green}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Equity {eqMode === "drops" ? "Drops" : "Growth"}</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>Top 10</div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setEqMode("drops")} style={{ background: eqMode === "drops" ? T.red : T.bgCard, border: `1px solid ${eqMode === "drops" ? T.red : T.border}`, borderRadius: 4, padding: "3px 6px", cursor: "pointer", display: "flex", alignItems: "center" }}>
                <ArrowDown size={12} color={eqMode === "drops" ? "#fff" : T.textMuted} />
              </button>
              <button onClick={() => setEqMode("gains")} style={{ background: eqMode === "gains" ? T.green : T.bgCard, border: `1px solid ${eqMode === "gains" ? T.green : T.border}`, borderRadius: 4, padding: "3px 6px", cursor: "pointer", display: "flex", alignItems: "center" }}>
                <ArrowUp size={12} color={eqMode === "gains" ? "#fff" : T.textMuted} />
              </button>
            </div>
          </div>
          {eqMode === "drops" ? (
            C.equityDrops.length === 0 ? (
              <div style={{ color: T.green, fontSize: 12, padding: 20, textAlign: "center" }}>No major drops ✓</div>
            ) : (
              <div>
                {C.equityDrops.slice(0, 10).map((c, i) => (
                  <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 11 }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <CoBadge co={c._company} />
                      <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ color: T.red, fontWeight: 600 }}>-{c.equityDrop}%</span>
                      <span style={{ color: T.textMuted, marginLeft: 4, fontSize: 9 }}>{fmtMoney(c.eqPrev)}→{fmtMoney(c.eqNow)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            (C.equityGains || []).length === 0 ? (
              <div style={{ color: T.textMuted, fontSize: 12, padding: 20, textAlign: "center" }}>No growth data</div>
            ) : (
              <div>
                {(C.equityGains || []).slice(0, 10).map((c, i) => (
                  <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 11 }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <CoBadge co={c._company} />
                      <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ color: T.green, fontWeight: 600 }}>+{c.equityGain}%</span>
                      <span style={{ color: T.textMuted, marginLeft: 4, fontSize: 9 }}>{fmtMoney(c.eqPrev)}→{fmtMoney(c.eqNow)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </Card>

        {/* ── Profit/Loss Toggle ── */}
        <Card accent={plMode === "profit" ? T.green : T.red}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Top 10 {plMode === "profit" ? "Profit" : "Loss"}</div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setPlMode("profit")} style={{ background: plMode === "profit" ? T.green : T.bgCard, border: `1px solid ${plMode === "profit" ? T.green : T.border}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 10, color: plMode === "profit" ? "#fff" : T.textMuted, fontWeight: 600 }}>
                Profit
              </button>
              <button onClick={() => setPlMode("loss")} style={{ background: plMode === "loss" ? T.red : T.bgCard, border: `1px solid ${plMode === "loss" ? T.red : T.border}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 10, color: plMode === "loss" ? "#fff" : T.textMuted, fontWeight: 600 }}>
                Loss
              </button>
            </div>
          </div>
          <div>
            {(plMode === "profit" ? C.topProfit : C.topLoss).slice(0, 10).map((c, i) => (
              <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CoBadge co={c._company} />
                  <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                </div>
                <span className="mono" style={{ color: plMode === "profit" ? T.green : T.red, fontWeight: 600 }}>
                  {plMode === "profit" ? fmtMoney(safeNum(c.Profit)) : "-" + fmtMoney(Math.abs(safeNum(c.Profit)))}
                </span>
              </div>
            ))}
          </div>
        </Card>

      </div>

      {/* ═══ ROW 4: DEPOSITS + VOLUME + BEHAVIORAL TREND ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
        <Card accent={T.green}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Deposits vs Withdrawals</div>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={C.monthlyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="deposit" fill={T.green} name="Deposits" radius={[2, 2, 0, 0]} />
              <Bar dataKey="withdraw" fill={T.red} name="Withdrawals" radius={[2, 2, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        <Card accent={T.cyan}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Volume Trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={C.monthlyStats}>
              <defs><linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.cyan} stopOpacity={0.3} /><stop offset="100%" stopColor={T.cyan} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="volume" stroke={T.cyan} fill="url(#volGrad)" name="Volume" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card accent={T.red}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Behavioral Risk Trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={C.monthlyStats}>
              <defs><linearGradient id="grad2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.red} stopOpacity={0.3} /><stop offset="100%" stopColor={T.red} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="highRisk" stroke={T.red} fill="url(#grad2)" name="High Risk" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ═══ ROW 5: ALERTS + TRADER DNA ═══ */}
      <div id="alerts-section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

        {/* Alerts — position absolute inside relative wrapper, matches DNA height */}
        <div style={{ position: "relative" }}>
          <Card accent={T.red} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.border}30`, flexShrink: 0 }}>
              <Bell size={16} color={T.red} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Alerts</span>
              <span style={{ fontSize: 11, color: T.red, fontWeight: 700, background: T.redBg, padding: "2px 8px", borderRadius: 10 }}>
                {alertFilter === "all" ? C.alerts.length : C.alerts.filter(a => a.type === alertFilter).length}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                {[
                  { key: "all", label: "All", color: T.text },
                  { key: "critical", label: "\u{1F534} Flags", color: T.red },
                  { key: "high", label: "\u{1F7E0} Equity", color: T.orange },
                  { key: "medium", label: "\u{1F7E1} Group", color: T.amber },
                ].map(f => (
                  <button key={f.key} onClick={() => setAlertFilter(f.key)}
                    style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontWeight: 600, border: "none",
                      background: alertFilter === f.key ? (f.key === "all" ? T.bgHover : `${f.color}25`) : "transparent",
                      color: alertFilter === f.key ? f.color : T.textMuted }}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const fa = alertFilter === "all" ? C.alerts : C.alerts.filter(a => a.type === alertFilter);
              return fa.length === 0 ? (
                <div style={{ color: T.green, fontSize: 13, textAlign: "center", padding: 30, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>No alerts ✓</div>
              ) : (
                <div style={{ overflowY: "auto", flex: 1 }}>
                  {fa.map((a, i) => (
                    <div key={i} onClick={() => onClientClick(C.clients.find(c => c.Login === a.login && c._company === a._company))}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 4, borderRadius: 8, cursor: "pointer",
                        background: a.type === "critical" ? T.redBg : a.type === "high" ? T.orangeBg : T.yellowBg }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "0.8"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                      <span style={{ fontSize: 12 }}>{a.icon}</span>
                      <CoBadge co={a._company} />
                      <span style={{ fontSize: 12, color: T.text, fontWeight: 600, flex: 1 }}>{a.name}</span>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {a.flags.map((f, j) => (
                          <span key={j} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, fontWeight: 600,
                            background: a.type === "critical" ? `${T.red}25` : a.type === "high" ? `${T.orange}25` : `${T.amber}25`,
                            color: a.type === "critical" ? T.red : a.type === "high" ? T.orange : T.amber }}>{f}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>
        </div>

        {/* DNA — natural height, determines the row size */}
        <Card accent={T.amber} style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Trader DNA</div>
            {dnaFilter && <button onClick={() => setDnaFilter(null)} style={{ fontSize: 10, padding: "3px 10px", borderRadius: 4, border: `1px solid ${T.border}`, background: T.bgCard, color: T.textMuted, cursor: "pointer", fontWeight: 600 }}>✕ Show All</button>}
          </div>
          {!dnaFilter ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {(() => {
                const dnaColors = { "Heavy Trader": T.red, "High Frequency Trader": T.orange, "Scalper": T.amber, "Hedger": T.cyan, "Hedging Abuser": "#ec4899", "Swaper": T.purple, "News Trader": T.blue, "Long Trader": T.green, "Gainer Trader": T.green, "Commissioner": "#f97316", "Arbitrage Suspected": "#ff0055" };
                const total = C.radarData.reduce((s, d) => s + d.count, 0);
                return C.radarData.filter(d => d.count > 0).sort((a, b) => b.count - a.count).map((d, i) => {
                  const col = dnaColors[d.type] || CHART_COLORS[i % CHART_COLORS.length];
                  return (
                    <div key={i} onClick={() => setDnaFilter(d.column)} style={{ background: T.bg, borderRadius: 10, padding: "18px 16px", cursor: "pointer", borderLeft: `4px solid ${col}`, transition: "all 0.15s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = T.bgHover; e.currentTarget.style.transform = "scale(1.02)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.transform = "scale(1)"; }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: col, fontWeight: 700 }}>{d.type}</span>
                        <span className="mono" style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{d.count}</span>
                      </div>
                      <div style={{ fontSize: 11, color: T.textDim, marginTop: 6 }}>{total > 0 ? (d.count / total * 100).toFixed(1) : 0}% of flagged</div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div style={{ overflowY: "auto", flex: 1 }}>
              {(() => {
                const arbCfg = getGroupConfig().arbitrage;
                const isArb = c => { const cds = (arbCfg.conditions||[]).filter(x=>x.enabled); if(!cds.length) return false; return cds.every(cd => { const v=safeNum(c[cd.column]); if(cd.op==="flag") return String(c[cd.column])===cd.value; if(cd.op===">=") return v>=cd.value; if(cd.op==="<") return v>0&&v<cd.value; if(cd.op===">") return v>cd.value; return false; }); };
                const fl = dnaFilter === "ArbitrageSuspected" ? C.clients.filter(isArb) : C.clients.filter(c => c[dnaFilter] === dnaFilter);
                const dn = dnaFilter.replace(/([A-Z])/g, ' $1').trim();
                return <>
                  <div style={{ fontSize: 13, color: T.amber, fontWeight: 600, marginBottom: 10 }}>{dn} — {fl.length} clients</div>
                  {fl.slice(0, 50).map((c, i) => (
                    <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 12 }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <CoBadge co={c._company} />
                        <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 22)}</span>
                      </div>
                      <span className="mono" style={{ color: T.textDim, fontSize: 10 }}>{c.Login}</span>
                    </div>
                  ))}
                </>;
              })()}
            </div>
          )}
        </Card>
      </div>

      {/* ═══ ROW 6: FTD ANALYSIS ═══ */}
      {C.ftdAnalysis && C.ftdAnalysis.totalFtdClients > 0 && (() => {
        const F = C.ftdAnalysis;
        const [ftdMode, setFtdMode] = [ftdTab, setFtdTab];
        return (<>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <DollarSign size={18} color={T.green} />
              <span style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>FTD Analysis <HelpTip id="ftdOverview" /></span>
              <span style={{ fontSize: 11, color: T.textDim }}>{F.totalFtdClients} clients with FTD data</span>
            </div>
            <div style={{ display: "flex", gap: 6, background: T.bg, borderRadius: 8, padding: 3 }}>
              {[
                { key: "country", label: "By Country" },
                { key: "timing", label: "Timing" },
                { key: "frequency", label: "Frequency" },
                { key: "correlation", label: "Correlation" },
              ].map(t => (
                <button key={t.key} onClick={() => setFtdTab(t.key)}
                  style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all 0.15s",
                    background: ftdTab === t.key ? T.green : "transparent", color: ftdTab === t.key ? "#fff" : T.textMuted }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── FTD Summary Cards ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            {[
              { label: "Avg FTD", value: fmtMoney(F.avgFtd), color: T.green },
              { label: "Median FTD", value: fmtMoney(F.medianFtd), color: T.cyan },
              { label: "Avg Time to FTD", value: F.ttfdAvgDays + " days", color: T.amber },
              { label: "Avg Deposits/Client", value: fmtNum(F.avgDepositCount, 1), color: T.purple },
            ].map(s => (
              <div key={s.label} style={{ background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}`, padding: "14px 16px", borderLeft: `4px solid ${s.color}` }}>
                <div style={{ fontSize: 11, color: s.color, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 }}>{s.label}</div>
                <div className="mono" style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* ── FTD Tab Content ── */}
          {ftdMode === "country" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <Card accent={T.green}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Average FTD by Country</div>
                <ResponsiveContainer width="100%" height={Math.max(200, F.ftdByCountry.length * 28)}>
                  <BarChart data={F.ftdByCountry} layout="vertical" margin={{ left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} />
                    <YAxis type="category" dataKey="country" tick={{ fontSize: 10, fill: T.text }} width={55} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="avgFtd" fill={T.green} name="Avg FTD ($)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card accent={T.cyan}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Country Breakdown — Deposit, Volume & Trades</div>
                <div style={{ overflowY: "auto", maxHeight: 400 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                        {["Country", "Clients", "Avg FTD", "Deposit", "Volume", "Trades"].map(h => (
                          <th key={h} style={{ padding: "6px 8px", textAlign: h === "Country" ? "left" : "right", color: T.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {C.countryStats.slice(0, 20).map((c, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.border}20` }}>
                          <td style={{ padding: "6px 8px", color: T.text, fontWeight: 600 }}>{c.country}</td>
                          <td className="mono" style={{ padding: "6px 8px", textAlign: "right", color: T.cyan }}>{c.clients}</td>
                          <td className="mono" style={{ padding: "6px 8px", textAlign: "right", color: T.green }}>{c.avgFtd > 0 ? fmtMoney(c.avgFtd) : "—"}</td>
                          <td className="mono" style={{ padding: "6px 8px", textAlign: "right", color: T.text }}>{fmtMoney(c.deposit)}</td>
                          <td className="mono" style={{ padding: "6px 8px", textAlign: "right", color: T.amber }}>{shortNum(Math.round(c.volume))}</td>
                          <td className="mono" style={{ padding: "6px 8px", textAlign: "right", color: T.purple }}>{shortNum(c.trades)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {ftdMode === "timing" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 20 }}>
              <Card accent={T.amber}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Time to First Deposit</div>
                  <span style={{ fontSize: 11, color: T.textDim }}>{F.ttfdBuckets.reduce((s, b) => s + b.count, 0)} clients with registration + FTD dates</span>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={F.ttfdBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: T.textDim }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Clients" radius={[4, 4, 0, 0]}>
                      {F.ttfdBuckets.map((b, i) => <Cell key={i} fill={i === 0 ? T.green : i <= 2 ? T.cyan : i <= 4 ? T.amber : T.orange} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", gap: 16, marginTop: 10, justifyContent: "center" }}>
                  {[{ label: "Same Day", color: T.green }, { label: "≤1 Week", color: T.cyan }, { label: "≤1 Month", color: T.amber }, { label: ">1 Month", color: T.orange }].map(l => (
                    <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} /> {l.label}
                    </span>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {ftdMode === "frequency" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 20 }}>
              <Card accent={T.purple}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Deposit Frequency Distribution</div>
                  <span style={{ fontSize: 11, color: T.textDim }}>{F.depFreqBuckets.reduce((s, b) => s + b.count, 0)} clients</span>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={F.depFreqBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: T.textDim }} label={{ value: "Number of Deposits", position: "insideBottom", offset: -5, fontSize: 11, fill: T.textMuted }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} label={{ value: "Clients", angle: -90, position: "insideLeft", fontSize: 11, fill: T.textMuted }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Clients" fill={T.purple} radius={[4, 4, 0, 0]}>
                      {F.depFreqBuckets.map((b, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}

          {ftdMode === "correlation" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <Card accent={T.green}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>FTD vs Volume (Top 200)</div>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={F.ftdCorrelation}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="ftd" tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} label={{ value: "First Deposit ($)", position: "insideBottom", offset: -5, fontSize: 11, fill: T.textMuted }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} label={{ value: "Volume (lots)", angle: -90, position: "insideLeft", fontSize: 11, fill: T.textMuted }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="volume" fill={T.cyan} name="Volume" opacity={0.7} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
              <Card accent={T.amber}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>FTD vs Trades (Top 200)</div>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={F.ftdCorrelation}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="ftd" tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} label={{ value: "First Deposit ($)", position: "insideBottom", offset: -5, fontSize: 11, fill: T.textMuted }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={shortNum} label={{ value: "Total Trades", angle: -90, position: "insideLeft", fontSize: 11, fill: T.textMuted }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="trades" fill={T.amber} name="Trades" opacity={0.7} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}
        </>);
      })()}
    </div>
  );
}
